import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {hooks} from '../hooks';
import {svg} from '../assets/svg';
import {RootState} from '../store';
import {components} from '../components';
import {Routes} from '../routes';
import {TabScreens} from '../routes';
import {setScreen} from '../store/slices/tabSlice';
import { axiosInstance } from '../api/api';
import { logout } from '../store/slices/permissionSlice';

type Props = {
  title?: string;
  userName?: boolean;
  userPhoto?: boolean;
  showGoBack?: boolean;
  showGoBack2?: boolean;
  showBasket?: boolean;
  headerStyle?: React.CSSProperties;
};

interface UserData {
  id: number;
  user_id: number;
  employee_personal?: {
    first_name: string;
    full_name: string;
    profile_picture_url: string | null;
  };
  user?: {
    email: string;
    name: string;
  };
}

const modalMenu = [
  {
    id: 1,
    title: 'Data Pribadi',
    route: Routes.EditProfile,
    switch: false,
  },
  // {
  //   id: 2,
  //   title: 'My orders',
  //   route: Routes.OrderHistory,
  //   switch: false,
  // },
  // {
  //   id: 3,
  //   title: 'Promocodes & gift cards',
  //   route: Routes.Promocodes,
  //   switch: false,
  // },
  // {
  //   id: 4,
  //   title: 'Notifications',
  //   route: '',
  //   switch: true,
  // },
  // {
  //   id: 5,
  //   title: 'Face ID',
  //   route: '',
  //   switch: true,
  // },
  // {
  //   id: 6,
  //   title: 'Support center',
  //   route: '',
  //   switch: false,
  // },
  {
    id: 7,
    title: 'Keluar',
    route: Routes.SignIn,
    switch: false,
  },
];

export const Header: React.FC<Props> = ({
  title,
  userName,
  userPhoto,
  showGoBack,
  showGoBack2,
  showBasket,
  headerStyle,
}) => {
  const navigate = hooks.useNavigate();
  const location = hooks.useLocation();
  const dispatch = hooks.useDispatch();

  const [showModal, setShowModal] = useState(false);
  const [themeColor, setThemeColor] = useState('#F6F9F9');
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');

    if (metaThemeColor && themeColor) {
      metaThemeColor.setAttribute('content', themeColor);
    }
  }, [themeColor, showModal]);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setIsLoading(true);
        const authToken = localStorage.getItem('authToken');
        
        if (!authToken) {
          return;
        }

        const sessionCredentials = sessionStorage.getItem('dpp_temp_credentials');
        const userUUID = sessionCredentials 
          ? JSON.parse(sessionCredentials).uuid 
          : localStorage.getItem('userUUID');
        
        if (userUUID) {
          try {
            const response = await axiosInstance.get(`/api/ap/v1/employee/${userUUID}`);
            
            if (response.data) {
              setUserData(response.data);
              localStorage.setItem('userData', JSON.stringify(response.data));
              return;
            }
          } catch (uuidError) {
            await loadUserDataFallback();
          }
        }
        await loadUserDataFallback();
        
      } catch (error) {
        loadCachedUserData();
      } finally {
        setIsLoading(false);
      }
    };

    const loadUserDataFallback = async () => {
      try {
        const sessionCredentials = sessionStorage.getItem('dpp_temp_credentials');
        const loginUsername = sessionCredentials ? JSON.parse(sessionCredentials).name : null;
        
        if (loginUsername) {
          const response = await axiosInstance.get("/api/ap/v1/employee", {
            params: { 
              search: loginUsername,
              per_page: 5
            }
          });

          if (response.data?.data?.length > 0) {
            const exactMatch = response.data.data.find((user: any) => 
              user.user?.name?.toLowerCase() === loginUsername.toLowerCase()
            );
            
            const employeeData = exactMatch || response.data.data[0];
            setUserData(employeeData);
            localStorage.setItem('userData', JSON.stringify(employeeData));
            return;
          }
        }

        handleSessionDataFallback();
        
      } catch (fallbackError) {
        handleSessionDataFallback();
      }
    };

    const loadCachedUserData = () => {
      try {
        const savedUserData = localStorage.getItem('userData');
        if (savedUserData) {
          setUserData(JSON.parse(savedUserData));
          return;
        }
        handleSessionDataFallback();
      } catch (cacheError) {
        handleSessionDataFallback();
      }
    };

    const handleSessionDataFallback = () => {
      const sessionCredentials = sessionStorage.getItem('dpp_temp_credentials');
      if (sessionCredentials) {
        const credentials = JSON.parse(sessionCredentials);
        const fallbackData: UserData = {
          id: 1,
          user_id: 1,
          employee_personal: {
            first_name: credentials.name || 'User',
            full_name: credentials.name || 'User',
            profile_picture_url: null,
          },
          user: {
            email: `${credentials.name}@security.com` || 'user@example.com',
            name: credentials.name || 'user'
          }
        };
        setUserData(fallbackData);
      }
    };

    loadUserData();
  }, []);

  const cart = useSelector((state: RootState) => state.cartSlice);

  const getUserName = (): string => {
    if (isLoading) return 'Loading...';
    if (!userData) return 'User';
    return userData.employee_personal?.full_name || 
           userData.employee_personal?.first_name || 
           userData.user?.name || 
           'User';
  };

  const getUserEmail = (): string => {
    if (isLoading) return 'Loading...';
    if (!userData) return 'user@example.com';
    return userData.user?.email || 'user@example.com';
  };

  const renderUser = (): JSX.Element | null => {
    if (!userName && !userPhoto) return null;

    return (
      <div
        style={{
          position: 'absolute',
          left: 16,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          cursor: 'pointer',
        }}
        onClick={() => {
          setShowModal(true);
          setThemeColor('#fff');
        }}
      >
        <div
          style={{
            width: 25,
            height: 25,
            borderRadius: '50%',
            backgroundColor: '#E6F3F8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 1px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ 
            transform: 'scale(0.8) translateY(1px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg.ProfileSvg />
          </div>
        </div>

        {userName && (
          <h5
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 500,
              color: '#0C1D2E',
              lineHeight: '1',
            }}
          >
            {getUserName()}
          </h5>
        )}
      </div>
    );
  };

  const renderGoBack = (): JSX.Element | null => {
    if (showGoBack && location.key !== 'default')
      return (
        <div
          onClick={() => navigate(-1)}
          style={{
            position: 'absolute',
            left: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '0 20px',
          }}
          className='clickable'
        >
          <svg.GoBackSvg />
        </div>
      );

    return null;
  };

  const renderGoBack2 = (): JSX.Element | null => {
    if (showGoBack2 && location.key !== 'default')
      return (
        <div
          onClick={() => navigate(-1)}
          style={{
            position: 'absolute',
            left: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '0 20px',
            backgroundColor: 'rgba(255, 255, 255)',
            borderRadius: 6,
          }}
          className='clickable'
        >
          <svg.GoBackSvg />
        </div>
      );

    return null;
  };

  const renderTitle = (): JSX.Element | null => {
    return (
      <div
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <span
          className='t16'
          style={{
            color: 'var(--main-color)',
            marginBottom: 2,
            display: 'block',
          }}
        >
          {title}
        </span>
      </div>
    );
  };

  // const renderBasket = (): JSX.Element | null => {
    //   if (!showBasket) return null;

    //   return (
    //     <button
    //       onClick={() => {
    //         dispatch(setScreen(TabScreens.Order));
    //         navigate(Routes.TabNavigator);
    //       }}
    //       style={{
    //         height: '100%',
    //         width: 'auto',
    //         padding: '0 20px',
    //         display: 'flex',
    //         alignItems: 'center',
    //         position: 'absolute',
    //         right: 0,
    //       }}
    //     >
    //       <div
    //         className='t10'
    //         style={{
    //           position: 'absolute',
    //           backgroundColor: 'var(--main-turquoise)',
    //           padding: '5px 4px 3px 4px',
    //           borderRadius: '12px',
    //           bottom: 9,
    //           right: 44,
    //           display: 'flex',
    //           alignItems: 'center',
    //           justifyContent: 'center',
    //           lineHeight: 1,
    //         }}
    //       >
    //         <span
    //           style={{
    //             color: 'var(--white-color)',
    //             fontFamily: 'DM Sans',
    //             fontWeight: 700,
    //             marginBottom: 1,
    //             fontSize: 10,
    //           }}
    //         >
    //           ${cart.total > 0 ? cart.total.toFixed(2) : '0'}
    //         </span>
    //       </div>
    //       <svg.HeaderBasketSvg />
    //     </button>
    //   );
    // };

  const renderModal = (): JSX.Element | null => {
    if (!showModal) return null;
    return (
      <>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(30, 37, 56, 0.6)',
            zIndex: 101,
            cursor: 'pointer',
          }}
          onClick={() => {
            setThemeColor('#F6F9F9');
            setShowModal(false);
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: '80%',
            backgroundColor: 'var(--white-color)',
            zIndex: 101,
          }}
        >
          <div
            style={{
              paddingTop: '20%',
              paddingLeft: 20,
              paddingRight: 20,
              marginBottom: 27,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 14,
              paddingBottom: 20,
              borderBottom: '1px solid #DBE9F5',
            }}
          >
            <div
              style={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                backgroundColor: '#E6F3F8',
                // display: 'flex',
                // alignItems: 'center',
                // justifyContent: 'center',
                flexShrink: 0,
                padding:2
              }}
            >
              <div style={{ 
                transform: 'scale(1.8) translateY(1.8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg.UserNoRectSvg />
              </div>
            </div>
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <span
                className='t14'
                style={{
                  color: 'var(--main-color)',
                  fontWeight: 500,
                  marginBottom: 4,
                }}
              >
                {getUserName()}
              </span>
              <span className='t14' style={{color: '#666'}}>{getUserEmail()}</span>
            </div>
          </div>

          <ul
            style={{
              paddingLeft: 20,
              paddingRight: 20,
              paddingBottom: 20,
            }}
          >
            <li
              className='row-center-space-between clickable'
              style={{
                paddingTop: 12,
                paddingBottom: 12,
                borderBottom: '1px solid #f0f0f0',
              }}
              onClick={() => {
                navigate(Routes.EditProfile);
                setThemeColor('#F6F9F9');
                setShowModal(false);
              }}
            >
              <span
                className='t16 number-of-lines-1'
                style={{color: 'var(--main-color)'}}
              >
                Data Pribadi
              </span>
              <div style={{transform: 'rotate(180deg)'}}>
                <svg.GoBackSvg />
              </div>
            </li>

            <li
              className='row-center-space-between clickable'
              style={{
                paddingTop: 12,
                paddingBottom: 12,
              }}
              onClick={() => {
                const rememberMe = localStorage.getItem('rememberMe') === 'true';

                localStorage.removeItem('authToken');
                localStorage.removeItem('userRole');
                localStorage.removeItem('patrol_group_id');
                localStorage.removeItem('userData');
                localStorage.removeItem('userUUID');
                localStorage.removeItem('permissions');
                localStorage.removeItem('patrol_id');
                localStorage.removeItem('patrol_ids');
                
                if (!rememberMe) {
                  sessionStorage.removeItem('dpp_temp_credentials');
                }
                dispatch(logout());
                
                navigate(Routes.Onboarding, { replace: true });
                setTimeout(() => {
                  navigate(Routes.SignIn);
                }, 0);

                setThemeColor('#F6F9F9');
                setShowModal(false);
              }}
            >
              <span
                className='t16 number-of-lines-1'
                style={{color: '#FA5555'}}
              >
                Keluar
              </span>
            </li>
          </ul>
        </div>
      </>
    );
  };

  return (
    <>
      <header
        style={{
          position: 'relative',
          height: 'var(--header-height)',
          backgroundColor: 'var(--main-background)',
          ...headerStyle,
        }}
        className='row-center-space-between'
      >
        {renderUser()}
        {renderGoBack()}
        {renderGoBack2()}
        {renderTitle()}
        {/* {renderBasket()} */}
      </header>
      {renderModal()}
    </>
  );
};